import { ref, onUnmounted } from 'vue'
import { useNotificationStore } from '@/stores/notification'
import { localizeAdminNotification } from '@/utils/adminNotificationI18n'
import { isDeletionJobAlreadyHandled } from '@/composables/useDeletionJobOutcome'
import { getAccessToken } from '@/utils/authTokens'
import {
  readPersistedLastId,
  persistLastId,
  resetAdminNotificationStreamCursor,
  isSyncNotificationDismissed
} from '@/utils/adminNotificationCursor'

export { resetAdminNotificationStreamCursor }

/** Mesma base que `api.js` */
function resolveApiBaseUrl() {
  const appBase = import.meta.env.BASE_URL || '/'
  const fromEnv = import.meta.env.VITE_API_BASE_URL?.trim()

  if (fromEnv) {
    const cleaned = fromEnv.replace(/\/$/, '')
    if (/^https?:\/\//i.test(cleaned)) return cleaned
    if (cleaned.startsWith('/') && !cleaned.startsWith('//')) {
      if (appBase === './' || appBase.startsWith('./')) return `.${cleaned}`
      if (appBase && appBase !== '/' && !appBase.startsWith('.')) {
        const pb = appBase.endsWith('/') ? appBase.slice(0, -1) : appBase
        return `${pb}${cleaned}`.replace(/\/{2,}/g, '/')
      }
      return cleaned
    }
    return cleaned
  }

  if (appBase === './' || appBase.startsWith('./')) return './api'
  const prefix = appBase.endsWith('/') ? appBase : `${appBase}/`
  if (prefix === '/') return '/api'
  return `${prefix}api`.replace(/\/{2,}/g, '/')
}

let sharedSource = null
let sharedLastId = readPersistedLastId()
let subscriberCount = 0
let isConnecting = false
let reconnectTimer = null
let reconnectAttempts = 0
const isConnected = ref(false)
const RECONNECT_BASE_MS = 5000
const RECONNECT_MAX_MS = 60000

function canConnectShared() {
  return (
    subscriberCount > 0 &&
    !!getAccessToken() &&
    document.visibilityState !== 'hidden'
  )
}

function scheduleReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
  }

  const delay = Math.min(
    RECONNECT_MAX_MS,
    RECONNECT_BASE_MS * 2 ** Math.min(reconnectAttempts, 4)
  )
  reconnectAttempts += 1

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (canConnectShared()) {
      connectShared()
    } else if (subscriberCount > 0 && getAccessToken()) {
      scheduleReconnect()
    }
  }, delay)
}

function handleSsePayload(data) {
  if (!data || typeof data !== 'object') return

  const eventId = typeof data.id === 'number' ? data.id : 0
  if (eventId > 0) {
    sharedLastId = Math.max(sharedLastId, eventId)
    persistLastId(sharedLastId)
  }

  const notif = data.notification
  if (!notif || typeof notif !== 'object') return

  const localized = localizeAdminNotification(notif)
  const payloadData = localized.data ?? null

  if (payloadData && isSyncNotificationDismissed(payloadData)) {
    return
  }

  const store = useNotificationStore()

  const syncJobId =
    payloadData?.kind === 'server_sync' ? String(payloadData.job_id ?? '') : ''
  if (syncJobId !== '') {
    const alreadyKnown = store.notifications.some(
      (n) => n.data?.kind === 'server_sync' && String(n.data?.job_id ?? '') === syncJobId
    )
    if (alreadyKnown) {
      return
    }
  }

  const provisionJobId =
    payloadData?.kind === 'account_provision' ? String(payloadData.job_id ?? '') : ''
  if (provisionJobId !== '') {
    const alreadyKnown = store.notifications.some(
      (n) =>
        n.data?.kind === 'account_provision' &&
        String(n.data?.job_id ?? '') === provisionJobId
    )
    if (alreadyKnown) {
      return
    }
  }

  const deletionJobId =
    payloadData?.kind === 'account_deletion' || payloadData?.kind === 'reseller_deletion'
      ? String(payloadData.job_id ?? '')
      : ''
  if (deletionJobId !== '') {
    if (isDeletionJobAlreadyHandled(deletionJobId)) {
      return
    }

    const alreadyKnown = store.notifications.some(
      (n) =>
        (n.data?.kind === 'account_deletion' || n.data?.kind === 'reseller_deletion') &&
        String(n.data?.job_id ?? '') === deletionJobId
    )
    if (alreadyKnown) {
      return
    }
  }

  const announcementId =
    payloadData?.kind === 'panel_announcement' ? String(payloadData.announcement_id ?? '') : ''
  if (announcementId !== '') {
    const alreadyKnown = store.notifications.some(
      (n) =>
        n.data?.kind === 'panel_announcement' &&
        String(n.data?.announcement_id ?? '') === announcementId
    )
    if (alreadyKnown) {
      window.dispatchEvent(new CustomEvent('dragonpanel:announcements-updated'))
      return
    }
  }

  const addedId = store.add({
    title: localized.title,
    message: localized.message,
    type: localized.type || 'info',
    data: payloadData,
    sseEventId: eventId > 0 ? eventId : null
  })

  if (addedId === null) {
    return
  }

  window.dispatchEvent(
    new CustomEvent('dragonpanel:admin-notification', { detail: localized })
  )

  if (payloadData?.kind === 'panel_announcement') {
    window.dispatchEvent(new CustomEvent('dragonpanel:announcements-updated'))
  }
}

async function connectShared() {
  const token = getAccessToken()
  if (!token || sharedSource || isConnecting) return

  sharedLastId = Math.max(sharedLastId, readPersistedLastId())

  const base = resolveApiBaseUrl().replace(/\/$/, '')
  const url = `${base}/notifications/stream?last_id=${sharedLastId}`
  const controller = new AbortController()
  isConnecting = true

  try {
    // Usar fetch com Authorization header (mais seguro que EventSource com token na URL)
    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    reconnectAttempts = 0

    if (!response.body) {
      throw new Error('No response body')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    // Simular EventSource manualmente para manter Authorization header
    const readStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read()
          
          if (done) {
            break
          }

          buffer += decoder.decode(value, { stream: true })

          let separatorIndex = buffer.indexOf('\n\n')
          while (separatorIndex !== -1) {
            const rawEvent = buffer.slice(0, separatorIndex)
            buffer = buffer.slice(separatorIndex + 2)
            separatorIndex = buffer.indexOf('\n\n')

            let event = ''
            let data = ''
            let id = ''

            for (const line of rawEvent.split('\n')) {
              const normalizedLine = line.replace(/\r$/, '')
              if (normalizedLine.startsWith('event: ')) {
                event = normalizedLine.substring(7).trim()
              } else if (normalizedLine.startsWith('data: ')) {
                data += normalizedLine.substring(6).trim()
              } else if (normalizedLine.startsWith('id: ')) {
                id = normalizedLine.substring(4).trim()
              }
            }

            if (id) {
              const numericId = Number(id)
              if (Number.isFinite(numericId) && numericId > sharedLastId) {
                sharedLastId = numericId
                persistLastId(sharedLastId)
              }
            }

            if (event === 'notification' && data) {
              try {
                handleSsePayload(JSON.parse(data))
              } catch {
                /* ignore */
              }
            } else if (event === 'connected' && data) {
              isConnected.value = true
              try {
                const payload = JSON.parse(data)
                const effective = Number(payload?.last_id ?? 0)
                const cursor = Number(payload?.server_cursor ?? 0)
                const store = useNotificationStore()
                let next = Math.max(sharedLastId, effective, cursor)

                // Se o sininho está vazio, avançar o cursor evita reenviar histórico antigo.
                if (store.notifications.length === 0 && cursor > sharedLastId) {
                  next = Math.max(next, cursor)
                }

                if (Number.isFinite(next) && next > sharedLastId) {
                  sharedLastId = next
                  persistLastId(sharedLastId)
                }
              } catch {
                /* ignore */
              }
            }
          }
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('SSE stream error:', error)
        }
        throw error
      } finally {
        reader.releaseLock()
      }
    }

    // Armazenar referência para poder cancelar
    sharedSource = {
      close: () => controller.abort()
    }
    isConnecting = false

    await readStream()
    isConnected.value = false
    sharedSource = null

    scheduleReconnect()

  } catch (error) {
    isConnected.value = false
    disconnectShared()
    scheduleReconnect()
  } finally {
    isConnecting = false
  }
}

function disconnectShared() {
  if (reconnectTimer && subscriberCount === 0) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (sharedSource) {
    if (typeof sharedSource.close === 'function') {
      sharedSource.close()
    }
    sharedSource = null
  }
}

export function resetSharedNotificationStreamState() {
  sharedLastId = 0
  reconnectAttempts = 0
  isConnected.value = false
  disconnectShared()
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

/**
 * SSE de notificações do admin (sincronização de servidor, etc.).
 */
export function useAdminNotificationStream() {
  function connect() {
    subscriberCount += 1
    if (canConnectShared()) {
      connectShared()
    }
  }

  function disconnect() {
    subscriberCount = Math.max(0, subscriberCount - 1)
    if (subscriberCount === 0) {
      disconnectShared()
      isConnected.value = false
    }
  }

  onUnmounted(() => {
    disconnect()
  })

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      disconnectShared()
      return
    }
    if (canConnectShared() && !sharedSource && !isConnecting) {
      reconnectAttempts = 0
      connectShared()
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return {
    isConnected,
    connect,
    disconnect
  }
}
